// ============================================================
// FIXED: SalesByCashierReportPage.tsx
// ============================================================
// src/features/reports/pages/SalesByCashierReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getSalesByCashier, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Users, DollarSign, ShoppingCart, TrendingUp,
    UserCheck, RotateCcw, Clock, Target
} from 'lucide-react';

export function SalesByCashierReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-sales-by-cashier', tenantId, params],
        queryFn: () => getSalesByCashier(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const formatNumber = (val: string | number) =>
        parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) {
            start.setHours(0, 0, 0, 0);
        } else {
            start.setDate(start.getDate() - days);
        }
        setDateRange({
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0],
        });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'sales_by_cashier', format, params as any);
        window.open(url, '_blank');
    };

    // FIXED: Proper boolean conversion
    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);

    const clearFilters = () => {
        setDateRange({ startDate: '', endDate: '' });
        setBranchId('');
    };

    const cashiers = data?.cashiers || [];
    const topCashier = cashiers[0];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30">
            <ReportHeader
                title="Sales by Cashier"
                description="Staff sales performance"
                icon={Users}
                iconGradient="from-purple-500 to-violet-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || cashiers.length === 0 ? (
                    <EmptyState
                        icon={<Users className="w-10 h-10" />}
                        title="No Cashier Sales Data"
                        description="No cashier sales found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <SummaryCard
                                label="Active Cashiers"
                                value={String(data.summary?.totalCashiers || 0)}
                                icon={Users}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Total Transactions"
                                value={formatNumber(data.summary?.totalSales || 0)}
                                icon={ShoppingCart}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Top Performer"
                                value={topCashier?.name || 'N/A'}
                                icon={UserCheck}
                                gradient="from-amber-500 to-orange-600"
                                subtitle={topCashier ? formatNaira(topCashier.revenue) : undefined}
                            />
                            <SummaryCard
                                label="Avg. Order Value"
                                value={formatNaira(data.summary?.totalSales > 0 ? parseFloat(data.summary.totalRevenue) / data.summary.totalSales : 0)}
                                icon={Target}
                                gradient="from-rose-500 to-pink-600"
                            />
                        </div>

                        <Card className="overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="text-sm font-semibold text-slate-800">Cashier Performance</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Cashier</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Sales</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Avg. Order</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Items Sold</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Voids</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Refunds</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Void Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {cashiers.map((cashier: any, i: number) => {
                                            const voidRate = cashier.sales + cashier.voidCount > 0
                                                ? (cashier.voidCount / (cashier.sales + cashier.voidCount) * 100).toFixed(1)
                                                : '0';
                                            return (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                                <span className="text-xs font-bold text-purple-600">
                                                                    {cashier.name?.charAt(0) || '?'}
                                                                </span>
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-900">{cashier.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm font-semibold">
                                                        {formatNumber(cashier.sales)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(cashier.revenue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm">
                                                        {formatNaira(cashier.avgOrderValue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">
                                                        {formatNumber(cashier.itemsSold)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-sm text-rose-600 font-medium">{cashier.voidCount}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-sm text-amber-600 font-medium">{cashier.refundCount}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge
                                                            variant={parseFloat(voidRate) > 5 ? 'danger' : parseFloat(voidRate) > 2 ? 'warning' : 'success'}
                                                            size="sm"
                                                        >
                                                            {voidRate}%
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}